import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Phone, Mail, MapPin, Clock, MessageCircle } from "lucide-react";
import {
  PHONE_PRIMARY, PHONE_WHATSAPP, PHONE_PRIMARY_TEL, EMAIL, CLINIC_ADDRESS, CLINIC_TIMINGS,
  GOOGLE_MAPS_EMBED, GOOGLE_MAPS_DIRECTIONS, getWhatsAppUrl, WHATSAPP_BOOKING_INTRO,
  TREATMENT_OPTIONS, PREFERRED_TIME_OPTIONS, CONSENT_TEXT, PRIVACY_LINE,
} from "@/lib/constants";
import { trackCall, trackWhatsApp, trackDirections, trackFormSubmit } from "@/lib/analytics";
import Breadcrumbs from "@/components/Breadcrumbs";

const Contact = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", phone: "", email: "", service: "", time: "", message: "", consent: false, honeypot: "" });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = () => {
    const e: Record<string, string> = {};
    if (!form.name.trim()) e.name = "Name is required";
    if (!form.phone.trim()) e.phone = "Phone is required";
    else if (!/^[6-9]\d{9}$/.test(form.phone.replace(/\s+/g, ""))) e.phone = "Enter valid 10-digit number";
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Enter valid email";
    if (!form.service) e.service = "Please select the treatment you need";
    if (!form.consent) e.consent = "Please tick the consent box so we can contact you";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (form.honeypot) return;
    if (!validate()) return;
    trackFormSubmit("contact_page", form.service);
    const msg = `${WHATSAPP_BOOKING_INTRO} ${form.service}.\nName: ${form.name}\nMobile: ${form.phone}${form.time ? `\nPreferred time: ${form.time}` : ""}${form.message ? `\nMessage: ${form.message}` : ""}\n(Source: Contact page)`;
    window.open(getWhatsAppUrl(msg), "_blank", "noopener,noreferrer");
    navigate("/thank-you");
  };

  return (
    <div>
      <section className="bg-secondary py-12 md:py-16">
        <div className="container">
          <Breadcrumbs items={[{ label: "Contact Us" }]} />
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-secondary-foreground">Contact Us</h1>
        </div>
      </section>

      <section className="py-16">
        <div className="container">
          <div className="grid md:grid-cols-2 gap-10">
            <div>
              <h2 className="font-heading text-xl font-bold mb-6">Send Us a Message</h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <input type="text" name="website" className="hidden" tabIndex={-1} autoComplete="off" value={form.honeypot} onChange={e => setForm(f => ({ ...f, honeypot: e.target.value }))} />
                <div>
                  <Label htmlFor="name">Full Name *</Label>
                  <Input id="name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} maxLength={100} />
                  {errors.name && <p className="text-destructive text-xs mt-1">{errors.name}</p>}
                </div>
                <div>
                  <Label htmlFor="phone">Mobile Number *</Label>
                  <Input id="phone" type="tel" inputMode="numeric" autoComplete="tel" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value.replace(/\D/g, "").slice(0, 10) }))} maxLength={10} />
                  {errors.phone && <p className="text-destructive text-xs mt-1">{errors.phone}</p>}
                </div>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} maxLength={255} />
                  {errors.email && <p className="text-destructive text-xs mt-1">{errors.email}</p>}
                </div>
                <div>
                  <Label>Treatment Needed *</Label>
                  <Select value={form.service} onValueChange={v => setForm(f => ({ ...f, service: v }))}>
                    <SelectTrigger aria-label="Treatment needed"><SelectValue placeholder="Select the treatment you need" /></SelectTrigger>
                    <SelectContent>
                      {TREATMENT_OPTIONS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {errors.service && <p className="text-destructive text-xs mt-1">{errors.service}</p>}
                </div>
                <div>
                  <Label>Preferred Time</Label>
                  <Select value={form.time} onValueChange={v => setForm(f => ({ ...f, time: v }))}>
                    <SelectTrigger aria-label="Preferred time"><SelectValue placeholder="Morning / Afternoon / Evening (optional)" /></SelectTrigger>
                    <SelectContent>
                      {PREFERRED_TIME_OPTIONS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="message">Tell us about your concern (optional)</Label>
                  <Textarea id="message" value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} maxLength={1000} rows={4} />
                </div>
                <label className="flex items-start gap-2 text-sm text-muted-foreground leading-relaxed">
                  <input type="checkbox" checked={form.consent} onChange={e => setForm(f => ({ ...f, consent: e.target.checked }))} className="mt-1 h-4 w-4 accent-[hsl(var(--primary))]" />
                  <span>{CONSENT_TEXT}</span>
                </label>
                {errors.consent && <p className="text-destructive text-xs">{errors.consent}</p>}
                <Button type="submit" size="lg" className="w-full h-12">Book My Consultation</Button>
                <p className="text-xs text-muted-foreground text-center">{PRIVACY_LINE}</p>
              </form>
            </div>

            <div className="space-y-6">
              <div>
                <h2 className="font-heading text-xl font-bold mb-6">Get in Touch</h2>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <Phone className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-sm">Phone</p>
                      <a href={PHONE_PRIMARY_TEL} onClick={() => trackCall("contact_page")} className="text-sm text-muted-foreground hover:text-primary">{PHONE_PRIMARY}</a>
                      <br />
                      <a href={getWhatsAppUrl()} onClick={() => trackWhatsApp("contact_page")} className="text-sm text-muted-foreground hover:text-primary">{PHONE_WHATSAPP} (WhatsApp)</a>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Mail className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-sm">Email</p>
                      <a href={`mailto:${EMAIL}`} className="text-sm text-muted-foreground hover:text-primary">{EMAIL}</a>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <MapPin className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-sm">Address</p>
                      <p className="text-sm text-muted-foreground">{CLINIC_ADDRESS}</p>
                      <a href={GOOGLE_MAPS_DIRECTIONS} target="_blank" rel="noopener noreferrer" onClick={() => trackDirections("contact_page")} className="text-sm text-primary hover:underline">Get directions →</a>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Clock className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-sm">Clinic Hours</p>
                      <div className="text-sm text-muted-foreground">
                        <p>Mon-Sat: {CLINIC_TIMINGS.weekdays}</p>
                        <p>Sunday: {CLINIC_TIMINGS.sunday}</p>
                        <p>Closed: {CLINIC_TIMINGS.closed}</p>
                      </div>
                    </div>
                  </li>
                </ul>
              </div>

              <div className="rounded-xl overflow-hidden h-64 bg-muted">
                <iframe
                  src={GOOGLE_MAPS_EMBED}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Mutha's Advanced Dental Care Clinic Location"
                />
              </div>

              <Button asChild className="w-full">
                <a href={getWhatsAppUrl(WHATSAPP_BOOKING_INTRO)} target="_blank" rel="noopener noreferrer" onClick={() => trackWhatsApp("contact_page_cta")}>
                  <MessageCircle className="h-5 w-5 mr-2" /> Chat on WhatsApp
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
